import { Component, OnDestroy, OnInit, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, Subscription } from 'rxjs';
import {
  QaiDragHandleDirective,
  QaiFieldComponent,
  QaiIconComponent,
  QaiPasteCleanDirective,
  QaiSearchableSelectComponent,
  QaiSearchableSelectOption,
  QaiTextareaComponent,
  QaiTrimLeadingDirective,
  TextareaAutosizeDirective,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { KnowledgeCheckAnswerPointVerb } from 'src/app/interfaces/self-serve/isimulation-details-information';

// One answer point row: drag handle | verb select | display-text textarea | delete.
// When uuid is empty on value change, emits createRetryRequested so the parent retries
// createGuidedAnswer. Content saves are handled by the parent via answerPoints.valueChanges.
@Component({
  selector: 'knowledge-check-answer-point-row',
  standalone: true,
  templateUrl: './knowledge-check-answer-point-row.component.html',
  styleUrls: ['./knowledge-check-answer-point-row.component.scss'],
  imports: [
    ReactiveFormsModule,
    QaiDragHandleDirective,
    QaiFieldComponent,
    QaiSearchableSelectComponent,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    QaiPasteCleanDirective,
    QaiIconComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class KnowledgeCheckAnswerPointRowComponent implements OnInit, OnDestroy {
  answerPointForm = input.required<FormGroup>();
  answerPointIndex = input.required<number>();
  readOnly = input(false);
  // True while this answer point's delete request is in flight.
  deleting = input(false);

  deleteRequested = output<{ index: number; uuid: string }>();
  createRetryRequested = output<void>();
  customFeedbackToggled = output<void>();

  callUuid: string = '';
  verbOptions: QaiSearchableSelectOption[] = [];
  customFeedbackExpanded = input(false);

  private valueSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.authorReactiveFormService.setReadOnlyMode(this.callUuid, this.answerPointForm().get('displayText'));

    this.verbOptions = [
      { value: KnowledgeCheckAnswerPointVerb.State,       label: this.translocoService.translate('simulationBuilder.KC_ANSWER_POINT_VERB_STATE') },
      { value: KnowledgeCheckAnswerPointVerb.Mention,     label: this.translocoService.translate('simulationBuilder.KC_ANSWER_POINT_VERB_MENTION') },
      { value: KnowledgeCheckAnswerPointVerb.Acknowledge, label: this.translocoService.translate('simulationBuilder.KC_ANSWER_POINT_VERB_ACKNOWLEDGE') },
    ];

    this.setupValueChange();
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
  }

  get verbValue(): string {
    return this.answerPointForm().get('verb')?.value ?? KnowledgeCheckAnswerPointVerb.State;
  }

  onVerbChange(value: string | string[]): void {
    this.answerPointForm().get('verb')?.setValue(value as string);
  }

  toggleCustomFeedback(): void {
    this.customFeedbackToggled.emit();
  }

  private setupValueChange(): void {
    this.valueSub = this.answerPointForm()
      .valueChanges.pipe(debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE))
      .subscribe(() => {
        if (!this.answerPointForm().get('uuid')?.value) {
          this.createRetryRequested.emit();
        }
      });
  }

  handleDelete(): void {
    this.deleteRequested.emit({
      index: this.answerPointIndex(),
      uuid: this.answerPointForm().get('uuid')?.value ?? '',
    });
  }
}
